import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTheme } from '@mui/material/styles';

import Grid from '@mui/material/Grid';
import { Typography, Card, CardContent, Box, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Main from 'layouts/Main';

const StudentCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-12px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 20px 40px rgba(74, 105, 255, 0.4)' 
      : '0 20px 40px rgba(74, 105, 255, 0.25)',
  },
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
  borderRadius: '20px',
  overflow: 'visible',
  position: 'relative',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.4)' 
    : '2px solid rgba(100, 150, 200, 0.5)',
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '120%',
  overflow: 'visible',
  borderRadius: '12px',
  margin: '12px',
  background: `linear-gradient(135deg, ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(100,150,200,0.05)'} 0%, ${theme.palette.mode === 'dark' ? 'rgba(100,150,200,0.1)' : 'rgba(200,220,255,0.1)'} 100%)`,
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.4)' 
    : '2px solid rgba(100, 150, 200, 0.3)',
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
    : 'linear-gradient(135deg, #c5e1ff 0%, #e8d5f2 50%, #d0f0ff 100%)',
  padding: '50px 20px',
  marginBottom: '50px',
  borderRadius: 0,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.6)'
    : '0 8px 32px rgba(0, 0, 0, 0.12)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'dark'
      ? 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.1) 0%, transparent 50%)'
      : 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.15) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
}));

const JeeResults2026 = () => {
  const theme = useTheme();
  const [config, setConfig] = useState({ base_image_url_2025: '' });

  useEffect(() => {
    fetch('/config.json')
      .then(response => response.json())
      .then(data => setConfig(data))
      .catch(error => console.error('Error fetching config:', error));
  }, []);

  const baseUrl = config.base_image_url_2025;

  const students = [
    // Rank 1 - Featured
    { id: 1, name: 'M SHIFAN KHAN', percentage: 99.838, air: 2677, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/1.webp` },
    // Other Toppers - Row 1
    { id: 2, name: 'KAUSHAL SURESH', percentage: 98.803, air: 5234, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/2.webp` },
    { id: 3, name: 'RANVIR CHOUDHARY', percentage: 98.441, air: 5689, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/3.webp` },
    { id: 4, name: 'K C SHREYAS', percentage: 97.745, air: 6125, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/4.webp` },
    // Other Toppers - Row 2
    { id: 5, name: 'S AISHVARYA', percentage: 93.87, air: 8521, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/10.webp` },
    { id: 6, name: 'RITAPRABHA SIL', percentage: 93.209, air: 8432, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/5.webp` },
    { id: 7, name: 'SAJEEV SIVASANKAR', percentage: 93.11, air: 8521, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/6.webp` },
    { id: 8, name: 'PRANAV R VARMA', percentage: 91.3, air: 8521, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/8.webp` },
    { id: 9, name: 'SAHANA S', percentage: 90.88, air: 8521, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/9.webp` },
    { id: 10, name: 'DANISH MOHAMMAD UMAR DUSGI', percentage: 90.385, air: 10234, image: `${baseUrl}/home/achievements2026/jee-toppers-2026/7.webp` },
  ];

  const topStudent = students[0];
  const secondRowStudents = students.slice(1, 4);
  const thirdRowStudents = students.slice(4, 7);
  const fourthRowStudents = students.slice(7,10);

  const renderStudentCard = (student, showAir = false) => (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      key={student.id}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
      }}
    >
      <StudentCard sx={{ width: '100%', maxWidth: '240px' }}>
        <ImageWrapper>
          <img
            src={student.image}
            alt={student.name}
            loading="lazy"
          />
        </ImageWrapper>
        <CardContent sx={{ pb: 2.5, pt: 2.5, px: 2, textAlign: 'center' }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              mb: showAir ? 1 : 1.5,
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
              color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a2e',
              lineHeight: 1.2,
              letterSpacing: '0.5px',
              minHeight: showAir ? 'auto' : '2.4em'
            }}
          >
            {student.name}
          </Typography>
          <Box sx={{ mb: showAir ? 0.75 : 1.5 }}>
            <Typography
              sx={{
                fontWeight: 800,
                color: theme.palette.primary.main,
                fontSize: { xs: '1rem', sm: '1.05rem', md: '1.2rem' }
              }}
            >
              {student.percentage}%ile
            </Typography>
          </Box>
          {showAir && (
            <Typography
              sx={{
                fontWeight: 900,
                color: theme.palette.mode === 'dark' ? '#ff8a8a' : '#d32f2f',
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                lineHeight: 1.2,
              }}
            >
              AIR {student.air}
            </Typography>
          )}
        </CardContent>
      </StudentCard>
    </Grid>
  );

  return (
    <Main>
      <Helmet>
        <title>NPS YPR JEE MAIN TOPPERS 2025-26</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa' }}>
        {/* Header Section */}
        <HeaderSection elevation={0}>
          <Box sx={{ textAlign: 'center', mb: 2, position: 'relative', zIndex: 1 }}>
        
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                color: theme.palette.primary.main,
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.4rem' },
                textShadow: theme.palette.mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none'
              }}
            >
              JEE Main Toppers 2025-26
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                maxWidth: '980px',
                mx: 'auto',
                color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#333',
                fontSize: { xs: '0.98rem', sm: '1.05rem', md: '1.12rem' },
                lineHeight: 1.8,
                textAlign: { xs: 'left', md: 'center' },
              }}
            >
              With immense pride and joy, we celebrate the remarkable achievement of our student,<b> M Shifan Khan</b>, who has secured an outstanding <b>99.838 percentile </b> and an <b>  All India Rank of 2677 </b> in the <b>JEE Examination 2026</b>.
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Button
              href="/jee-advanced-results-2026/"
              variant="contained"
              size="large"
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
              }}
            >
              View JEE Advanced Results
            </Button>
          </Box>
        </HeaderSection>

        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 }, mb: 4 }}>
          <Grid
            container
            spacing={{ xs: 2, sm: 2.5, md: 3, lg: 4 }}
            sx={{
              justifyContent: 'center',
              display: 'flex',
              width: '100%',
            }}
          >
            {renderStudentCard(topStudent, true)}
          </Grid>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mb: 4 }}>
          <Typography
            variant="body1"
            sx={{
              maxWidth: '980px',
              mx: 'auto',
              color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#333',
              fontSize: { xs: '0.98rem', sm: '1.05rem', md: '1.12rem' },
              lineHeight: 1.8,
              textAlign: { xs: 'left', md: 'center' },
            }}
          >
          It is with immense pride and joy that we celebrate the remarkable achievement of our students who have performed exceptionally well in the prestigious <strong>JEE Examination 2026</strong>. These outstanding accomplishments are a true reflection of their dedication, perseverance, discipline, and relentless pursuit of excellence.
          </Typography>
        </Box>

        {/* Other Toppers Section */}
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 }, pb: 8 }}>
          <Grid
            container
            spacing={{ xs: 2, sm: 2.5, md: 3, lg: 4 }}
            sx={{
              justifyContent: 'center',
              display: 'flex',
              width: '100%',
              mb: 4,
            }}
          >
            {secondRowStudents.map((student) => renderStudentCard(student))}
          </Grid>
          <Grid
            container
            spacing={{ xs: 2, sm: 2.5, md: 3, lg: 4 }}
            sx={{
              justifyContent: 'center',
              display: 'flex',
              width: '100%',
            }}
          >
            {thirdRowStudents.map((student) => renderStudentCard(student))}
          </Grid>
          <Grid
            container
            spacing={{ xs: 2, sm: 2.5, md: 3, lg: 4 }}
            sx={{
              justifyContent: 'center',
              display: 'flex',
              width: '100%',
            }}
          >
            {fourthRowStudents.map((student) => renderStudentCard(student))}
          </Grid>
        </Box>
      </Box>
    </Main>
  );
};

export default JeeResults2026;
